import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Key } from "lucide-react"
import * as React from "react"

import { SkeletonText } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { DataTable, type Column } from "@/components/ui/data-table"
import { Dialog, DialogTrigger } from "@/components/ui/dialog"
import { EmptyState } from "@/components/ui/empty-state"
import { useToast } from "@/components/ui/toast"
import {
  apiKeysQuery,
  CreatedCell,
  CreateKeyDialog,
  KeyCell,
  LastUsedCell,
  RevealKeyDialog,
  RevokeKeyCell,
  RevokeKeyDialog,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
  type CreatedApiKey,
  type ServiceKey,
} from "@/features/users-keys"

/*
   Integration keys: what a machine sends as X-API-Key. Admin only, as the backend's
   /api/v1/api-keys is. The full key is shown once, on creation; the backend keeps only its hash.
*/

export function IntegrationKeys() {
  const qc = useQueryClient()
  const keys = useQuery(apiKeysQuery(qc))
  const create = useCreateApiKeyMutation()
  const revoke = useRevokeApiKeyMutation()
  const { toast } = useToast()
  const [createOpen, setCreateOpen] = React.useState(false)
  const [revealed, setRevealed] = React.useState<CreatedApiKey | null>(null)
  const [pendingRevoke, setPendingRevoke] = React.useState<ServiceKey | null>(null)

  const columns = React.useMemo<Column<ServiceKey>[]>(
    () => [
      { id: "key", header: "Key", width: "minmax(0,1.4fr)", cell: (k) => <KeyCell item={k} /> },
      { id: "created", header: "Created", width: "180px", cell: (k) => <CreatedCell item={k} /> },
      {
        id: "lastUsed",
        header: "Last used",
        width: "140px",
        cell: (k) => <LastUsedCell iso={k.lastUsedAt} />,
      },
      {
        id: "revoke",
        header: "",
        width: "56px",
        align: "end",
        cell: (k) => (
          <RevokeKeyCell
            item={k}
            onRevoke={() => {
              setPendingRevoke(k)
            }}
          />
        ),
      },
    ],
    [],
  )

  return (
    <section className="rounded-lg border border-border bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div>
          <h2 className="text-heading text-text">Integration keys</h2>
          <p className="text-body-sm text-text-3">
            Sent as <code className="font-mono text-text">X-API-Key</code>. Name each after what
            uses it, for example <code className="font-mono text-text">metrics-publisher</code>.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button variant="primary" size="sm" leadingIcon={<Key />}>
              Create key
            </Button>
          </DialogTrigger>
          <CreateKeyDialog
            isSubmitting={create.isPending}
            onSubmit={(name) => {
              create.mutate(name, {
                onSuccess: (created) => {
                  setCreateOpen(false)
                  setRevealed(created)
                },
                onError: (err) => {
                  toast({ tone: "error", title: "The key was not created", body: err.message })
                },
              })
            }}
          />
        </Dialog>
      </div>

      <div className="border-t border-border">
        {keys.isPending && (
          <div className="p-4">
            <SkeletonText lines={3} />
          </div>
        )}
        {keys.isError && (
          <EmptyState
            reason="error"
            title="The keys could not be loaded"
            body={keys.error.message}
          />
        )}
        {keys.isSuccess && (
          <DataTable<ServiceKey>
            label="Integration keys"
            columns={columns}
            rows={keys.data}
            rowKey={(k) => k.id}
            className="rounded-none border-0"
            empty={
              <EmptyState bare title="No integration key yet" body="Create one to call the API." />
            }
          />
        )}
      </div>

      <Dialog
        open={revealed !== null}
        onOpenChange={(open) => {
          if (!open) setRevealed(null)
        }}
      >
        {revealed && (
          <RevealKeyDialog
            plaintext={revealed.plaintext}
            keyName={revealed.name}
            onDone={() => {
              setRevealed(null)
            }}
          />
        )}
      </Dialog>

      <Dialog
        open={pendingRevoke !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRevoke(null)
        }}
      >
        {pendingRevoke && (
          <RevokeKeyDialog
            serviceKey={pendingRevoke}
            onConfirm={() => {
              const key = pendingRevoke
              revoke.mutate(key.id, {
                onSuccess: () => {
                  toast({ tone: "success", title: `${key.name} revoked` })
                },
                onError: (err) => {
                  toast({ tone: "error", title: "The key was not revoked", body: err.message })
                },
              })
              setPendingRevoke(null)
            }}
          />
        )}
      </Dialog>
    </section>
  )
}
